import * as React from "react";
import { Link } from "react-router-dom";
import { CircleCheck, CircleDashed, CircleSlash, Loader } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  useSubscriptionState,
  type SubscriptionState,
} from "@/hooks/queries/useSubscriptionState";
import {
  ORG_LAPSE_CONTACT_ADMIN,
  isBillingBlockedPillState,
  isOrgSeatLapsed,
  selectSubscriptionPillState,
} from "@/lib/subscription-copy";
import { useCalibrationStatus } from "@/hooks/queries/useCalibrationStatus";
import type {
  CalibrationReviewStatus,
  CalibrationStatusData,
} from "@/hooks/queries/calibration-status-types";
import { CalibrationReviewNote } from "@/routes/calibration/review-flag-copy";

// Story 8.6 (gh-76) — Readiness Status Card. Clinician workspace-home surface
// (Screen 02 slot, WorkspaceHomeScreen) that tells the clinician, at a glance,
// whether they are AUTHORIZED for live scoring and — when not — exactly where in
// calibration they stand. The SAME component renders in three role variants
// (self / super_admin / org_admin); the numeric severities are ROLE-SCOPED on the
// BE payload (UX D3 / FR56), so this component is deliberately render-what-you-get:
// it renders motor/process numbers IFF the wire carries them. An org_admin-shaped
// payload has null severities — the logits never reach that browser and nothing
// numeric renders here. Security lives in the BE payload shaping, not the UI.
//
// AC2 / NFR29 / WCAG 2.1 AA: every state carries a NON-COLOUR indicator (icon +
// text). The status pill's colour is redundant, never the sole signal.
//
// Tokens only (no hardcoded hex, no pure-white); severities render with
// `tabular-nums` and are constructed straight from the Decimal STRING on the wire
// — never `parseFloat`/`Number()` (the measure logits are precision-critical, see
// calibration-status-types.ts).

/**
 * The five card states (AC1). `authorized` ≡ complete + active subscription;
 * `blocked` = a subscription issue (lapsed / no entitlement). `complete` is
 * calibration done but the subscription is not confirmed-active (e.g. still
 * loading). `in_progress` shows "N of 5". `not_started` is the zero state.
 */
export type ReadinessCardState =
  | "authorized"
  | "blocked"
  | "complete"
  | "in_progress"
  | "not_started";

/**
 * Pure, unit-testable state derivation (AC1). Order matters:
 *   1. the shared pill mapper reports a BILLING-BLOCKED state
 *      (`isBillingBlockedPillState` — `lapsed` / `inactive` / `org_lapsed`) →
 *      `blocked`, regardless of calibration: a lapsed/inactive subscription
 *      blocks live scoring even for a fully-calibrated clinician. The
 *      pre-calibration state is a CALIBRATION concern, not billing, so the set
 *      excludes it and that state falls through to step 4's mirror instead
 *      (`loading` maps to no pill at all and is likewise never blocked).
 *      [formula-error sub-case of `blocked` is deferred to Story 8.5 — seam.]
 *   2. calibration complete + subscription active → `authorized`.
 *   3. calibration complete but subscription NOT confirmed-active → `complete`.
 *   4. otherwise mirror the calibration state (`in_progress` / `not_started`).
 */
export function deriveReadinessCardState(
  calibration: Pick<CalibrationStatusData, "state">,
  subscription: SubscriptionState,
): ReadinessCardState {
  // gh-556 — the pre-calibration inactive state is a CALIBRATION state, not a
  // billing block: `blocked`'s copy ("renew billing to restore live scoring")
  // would contradict what the same account is told everywhere else, which is now
  // one sentence rather than two — every surface renders the shared mapper's
  // "Activating after calibration" for this state (`lib/subscription-copy.ts`).
  // This card is the third consumer the ledger's consolidation entry named: it
  // used to restate the rule as `isSubscriptionLapsed && !isPreCalibrationSubscription`,
  // which was a fourth place the same question could drift. Every other inactive
  // state (org pre-start, true lapse, never-subscribed) keeps `blocked` — its
  // copy says "subscription is inactive", accurate for all.
  if (isBillingBlockedPillState(selectSubscriptionPillState({ subscription }))) {
    return "blocked";
  }

  if (calibration.state === "complete") {
    return subscription.status === "active" ? "authorized" : "complete";
  }

  return calibration.state === "in_progress" ? "in_progress" : "not_started";
}

interface StatePresentation {
  /** Non-colour glyph paired with the label — the primary signal (AC2). */
  Icon: React.ComponentType<{ className?: string; "aria-hidden"?: boolean | "true" | "false" }>;
  /** Full-sentence heading. */
  label: string;
  description: string;
  /**
   * Concise status token inside the pill. The pill is redundant colour
   * reinforcement, but STILL carries text (WCAG "more than colour") — so the
   * signal survives with colour stripped. Distinct from `label` so the heading
   * and pill read as heading + tag, not the same sentence twice.
   */
  pillLabel: string;
  /** Badge colour is redundant reinforcement only. */
  badgeVariant: React.ComponentProps<typeof Badge>["variant"];
}

/**
 * The `blocked` lead-in, shared by the base description and the org-seat tail
 * swap in ReadinessCardView (gh-568, PR-584 review) — single-sourced so an edit
 * to the base copy cannot leave the org branch carrying stale text.
 */
const BLOCKED_LEAD_IN =
  "Your subscription is inactive. Existing records stay available in read-only mode";

const STATE_PRESENTATION: Record<ReadinessCardState, StatePresentation> = {
  authorized: {
    Icon: CircleCheck,
    label: "Authorized for live scoring",
    description: "Calibration is complete and your subscription is active.",
    pillLabel: "Authorized",
    badgeVariant: "ok",
  },
  complete: {
    Icon: CircleCheck,
    label: "Calibration complete",
    description: "Your motor and process measures are set.",
    pillLabel: "Complete",
    badgeVariant: "ok",
  },
  in_progress: {
    Icon: Loader,
    label: "Calibration in progress",
    description: "Finish the remaining calibration cases to unlock live scoring.",
    pillLabel: "In progress",
    badgeVariant: "info",
  },
  not_started: {
    Icon: CircleDashed,
    label: "Calibration not started",
    description: "Complete calibration to become authorized for live scoring.",
    pillLabel: "Not started",
    badgeVariant: "neutral",
  },
  blocked: {
    Icon: CircleSlash,
    label: "Live scoring blocked",
    description: `${BLOCKED_LEAD_IN}; renew billing to restore live scoring.`,
    pillLabel: "Blocked",
    badgeVariant: "blocked",
  },
};

/** "N of 5" progress line for the in-progress state (5 fixed calibration cases). */
function progressLabel(completedCases: number): string {
  const clamped = Math.max(0, Math.min(5, completedCases));
  return `${clamped} of 5 cases complete`;
}

/**
 * The collapsed-by-default own-numerics panel (AC4). Rendered only in the
 * `authorized` state, and only when the payload actually carries the severities —
 * an org_admin payload (null severities) renders NOTHING numeric here. Values are
 * read-only, framed as neutral numeric outputs (not pass/fail), and printed
 * verbatim from the wire STRING with `tabular-nums`.
 */
function OwnSeverities({
  motorSeverity,
  processSeverity,
  reviewStatus,
}: {
  motorSeverity: string;
  processSeverity: string;
  reviewStatus?: CalibrationReviewStatus | null;
}) {
  const [open, setOpen] = React.useState(false);
  const panelId = React.useId();

  return (
    <div className="flex flex-col gap-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={panelId}
        className="inline-flex w-fit items-center gap-1 rounded-lg text-sm font-semibold text-[color:var(--text-brand-secondary)] underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--focus-ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--bg-primary)]"
      >
        {open ? "Hide your measures" : "Show your measures"}
      </button>
      {open ? (
        <div id={panelId} className="flex flex-col gap-3">
          <dl
            className="grid grid-cols-1 gap-3 sm:grid-cols-2"
            aria-label="Your calibration measures"
          >
            <div className="rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-secondary)] px-4 py-3">
              <dt className="text-sm text-[color:var(--text-tertiary)]">Motor severity</dt>
              <dd className="tabular-nums mt-1 text-xl font-semibold text-[color:var(--text-primary)]">
                {motorSeverity}
              </dd>
            </div>
            <div className="rounded-2xl border border-[color:var(--border-primary)] bg-[color:var(--bg-secondary)] px-4 py-3">
              <dt className="text-sm text-[color:var(--text-tertiary)]">Process severity</dt>
              <dd className="tabular-nums mt-1 text-xl font-semibold text-[color:var(--text-primary)]">
                {processSeverity}
              </dd>
            </div>
          </dl>
          <CalibrationReviewNote status={reviewStatus} />
        </div>
      ) : null}
    </div>
  );
}

/**
 * Presentational card body — split out so it renders from a resolved
 * (calibration, subscription) pair regardless of how they were fetched.
 */
function ReadinessCardView({
  calibration,
  subscription,
}: {
  calibration: CalibrationStatusData;
  subscription: SubscriptionState;
}) {
  const state = deriveReadinessCardState(calibration, subscription);
  const { Icon, label, description: baseDescription, pillLabel, badgeVariant } =
    STATE_PRESENTATION[state];
  // gh-568 review F2 — the `blocked` copy's "renew billing…" tail is a self-serve
  // instruction an org-seat clinician cannot follow (B2B renewal is off-platform);
  // swap it for the shared contact-your-administrator sentence, keeping the
  // shared lead-in. Every other state keeps its static copy.
  const description =
    state === "blocked" && isOrgSeatLapsed(subscription)
      ? `${BLOCKED_LEAD_IN}. ${ORG_LAPSE_CONTACT_ADMIN}`
      : baseDescription;

  // Render own numerics IFF present in the payload (render-what-you-get; the BE
  // shapes the payload per role). Both fields are populated together or absent
  // together, but gate on each to be defensive against a partial wire.
  const hasNumerics =
    calibration.motor_severity != null && calibration.process_severity != null;

  return (
    <section
      className="flex flex-col gap-4 rounded-3xl border border-[color:var(--border-primary)] bg-[color:var(--bg-primary)] p-6 shadow-elevated"
      aria-label="Readiness status"
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="flex items-start gap-3">
          <Icon
            aria-hidden="true"
            className="mt-0.5 h-5 w-5 shrink-0 text-[color:var(--text-tertiary)]"
          />
          <div className="flex flex-col gap-1">
            <h2 className="text-lg font-semibold text-[color:var(--text-primary)]">{label}</h2>
            <p className="text-sm text-[color:var(--text-tertiary)]">{description}</p>
          </div>
        </div>
        {/* Colour pill is redundant reinforcement; the icon + label above carry meaning. */}
        <Badge variant={badgeVariant}>{pillLabel}</Badge>
      </div>

      {state === "in_progress" ? (
        <div className="flex flex-wrap items-center justify-between gap-3">
          <p className="tabular-nums text-sm text-[color:var(--text-secondary)]">
            {progressLabel(calibration.completed_cases)}
          </p>
          {/* AC3 — the primary CTA. Deep-link to the next incomplete case is deferred
              to Story 8.7 (seam); the 8.1 overview route is the entry point today. */}
          <Button asChild variant="primary">
            <Link to="/calibration/overview">Continue calibration</Link>
          </Button>
        </div>
      ) : null}

      {state === "authorized" && hasNumerics ? (
        <OwnSeverities
          motorSeverity={calibration.motor_severity as string}
          processSeverity={calibration.process_severity as string}
          reviewStatus={calibration.calibration_review_status}
        />
      ) : null}
    </section>
  );
}

/**
 * The mounted card (AC8). Owns its own data reads: the calibration status query
 * and the subscription state. While the calibration read is pending, render
 * nothing (the home already reads as intentional without a placeholder box — see
 * WorkspaceHomeScreen). A failed/absent read likewise renders nothing rather than
 * a misleading state — the BE gate is the authority for live scoring.
 */
export function ReadinessStatusCard() {
  const subscription = useSubscriptionState();
  const calibrationQuery = useCalibrationStatus({ enabled: true });

  const calibration = calibrationQuery.data;
  if (!calibration) return null;

  return <ReadinessCardView calibration={calibration} subscription={subscription} />;
}

export default ReadinessStatusCard;
